import React from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import ecStat from 'echarts-stat';
import {
  Container,
  Grid,
  Segment,
  Statistic,
  Icon,
  Loader,
  Message,
  Popup
} from 'semantic-ui-react';

import { useGetPKSalesAlignmentQuery } from '../evaluation_sessions/evaluationSessionApiSlice';

echarts.registerTransform(ecStat.transform.histogram);

// Helper for Boxplot Calculation
const calculateBoxplotStats = (data) => {
    if (!data || data.length === 0) return { boxData: [0,0,0,0,0], outliers: [] };
    
    const sorted = [...data].sort((a, b) => a - b);
    const q1 = quantile(sorted, 0.25);
    const median = quantile(sorted, 0.5);
    const q3 = quantile(sorted, 0.75);
    const iqr = q3 - q1;
    const minLimit = q1 - 1.5 * iqr;
    const maxLimit = q3 + 1.5 * iqr;
    
    const outliers = [];
    const validData = [];
    
    sorted.forEach(v => {
        if (v < minLimit || v > maxLimit) {
            outliers.push(v);
        } else {
            validData.push(v);
        }
    });

    const min = validData.length > 0 ? validData[0] : q1;
    const max = validData.length > 0 ? validData[validData.length - 1] : q3;

    return {
        boxData: [min, q1, median, q3, max],
        outliers
    };
};


const quantile = (sorted, q) => {
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    if (sorted[base + 1] !== undefined) {
        return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
    } else {
        return sorted[base];
    }
};

const PKSalesAnalytics = () => {
  const { data, isLoading, isError, error } = useGetPKSalesAlignmentQuery();

  if (isLoading) return <Loader active inline="centered" />;
  if (isError) return null;

  const { overview, scatter_data } = data;

  // Manual Binning Function
  const createHistogramData = (values, binSize = 5, maxVal = 105) => { // maxVal slightly higher to catch 100
    const bins = Math.ceil(maxVal / binSize);
    const counts = new Array(bins).fill(0);
    const labels = [];
    
    for (let i = 0; i < bins; i++) {
        labels.push(`${i * binSize}-${(i + 1) * binSize}`);
    }

    values.forEach(val => {
        const index = Math.min(Math.floor(val / binSize), bins - 1);
        if (index >= 0) counts[index]++;
    });

    return { counts, labels };
  };

  const pkScores = scatter_data.map(d => d.x);
  const salesScores = scatter_data.map(d => d.y);
  // Difference: PK - Sales. Positive means higher PK, Negative means higher Sales Skills
  const scoreDiffs = scatter_data.map(d => d.x - d.y); 
  
  const pkHist = createHistogramData(pkScores, 5); 
  const salesHist = createHistogramData(salesScores, 5);

  const createDiffHistogramData = (values, binSize = 5) => {
     let min = Infinity;
     let max = -Infinity;
     values.forEach(v => {
         if(v < min) min = v;
         if(v > max) max = v;
     });
     
     // Align to 0
     const minBinStart = Math.floor(min / binSize) * binSize;
     const maxBinEnd = Math.ceil(max / binSize) * binSize;
     const range = maxBinEnd - minBinStart;
     const binsCount = Math.ceil(range / binSize) || 1;
     
     const counts = new Array(binsCount).fill(0);
     const labels = [];
     
     for(let i=0; i<binsCount; i++) {
         const start = minBinStart + i * binSize;
         labels.push(`${start}`);
     }
     
     values.forEach(val => {
         const idx = Math.floor((val - minBinStart) / binSize);
         if(idx >= 0 && idx < binsCount) counts[idx]++;
     });
     
     return { counts, labels, minBinStart };
  };

  const diffHist = createDiffHistogramData(scoreDiffs, 5);

  // Prepare Data for ECharts

  // Format: [PK Score, Sales Score, Title]
  const sourceData = scatter_data.map(item => [item.x, item.y, item.product + " (" + item.id + ")"]);

  const scatterOption = {
    title: {
        text: '제품 지식 vs 판매 스킬 산점도',
        left: 'center',
        top: '0%',
        textStyle: {
            fontSize: 14,
            fontWeight: 600,
            color: '#1d1d1f',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }
    },
    dataset: [], 
    legend: {
        top: '5%',
        right: '5%',
        orient: 'vertical',
        textStyle: {
            color: '#86868b',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        },
        data: ['개별 세션', '제품 지식(PK)', '판매 스킬'] 
    },
    tooltip: {
      trigger: 'item',
      axisPointer: { type: 'cross' },
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderColor: 'rgba(0, 0, 0, 0.1)',
      formatter: (params) => {
        if (params.seriesName === '개별 세션') {
          const data = Array.isArray(params.data) ? params.data : [];
          if (data.length >= 3) {
             const [pk, sales, title] = data;
             return `<b>${title}</b><br/>PK 점수: ${pk}<br/>판매 스킬: ${sales}`;
          }
          return '';
        }
        if (params.value !== undefined) {
           return `Range: ${params.name}<br/>Count: ${params.value}`;
        }
        return '';
      }
    },
    grid: [
      {
        top: '25%',
        right: '25%',
        bottom: '10%',
        left: '10%',
        containLabel: false
      },
      {
        // Top Hist (X-axis: PK)
        bottom: '75.5%', 
        right: '25%',
        left: '10%',
        top: '5%',
        containLabel: false
      },
      {
        // Right Hist (Y-axis: Sales)
        top: '25%',
        left: '75.5%',
        bottom: '10%',
        right: '5%',
        containLabel: false
      }
    ],
    xAxis: [
      {
        type: 'value',
        scale: false,
        min: 0,
        max: 100,
        gridIndex: 0,
        name: '제품 지식 (PK) 점수',
        nameLocation: 'middle',
        nameGap: 30,
        axisLine: { show: true, lineStyle: { color: '#86868b' } },
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } }
      },
      {
        // Top Histogram X
        type: 'category',
        gridIndex: 1,
        data: pkHist.labels,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        boundaryGap: true
      },
      {
        // Right Histogram X (Count)
        type: 'value',
        gridIndex: 2,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false }
      }
    ],
    yAxis: [
      {
        type: 'value',
        scale: false,
        min: 0,
        max: 100,
        gridIndex: 0,
        name: '판매 스킬 점수',
        nameLocation: 'middle',
        nameGap: 30,
        axisLine: { show: true, lineStyle: { color: '#86868b' } },
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } }
      },
      {
        // Top Histogram Y (Count)
        type: 'value',
        gridIndex: 1,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false }
      },
      {
        // Right Histogram Y (Bin Categories)
        type: 'category',
        gridIndex: 2,
        data: salesHist.labels,
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        boundaryGap: true
      }
    ],
    series: [
      {
        name: '개별 세션',
        type: 'scatter',
        xAxisIndex: 0,
        yAxisIndex: 0,
        data: sourceData,
        itemStyle: {
            color: 'rgba(255, 149, 0, 0.6)', // Orange for variation
            borderColor: 'rgba(255, 149, 0, 1)'
        },
        markLine: {
            symbol: ['none', 'none'],
            silent: true,
            lineStyle: { type: 'dashed', color: 'rgba(134, 134, 139, 0.5)' },
            data: [
               // Diagonal line x=y to show perfect alignment
                [
                    { coord: [0, 0], symbol: 'none' },
                    { coord: [100, 100], symbol: 'none' }
                ]
            ]
        }
      },
      {
        name: '제품 지식(PK)',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: pkHist.counts,
        barWidth: '95%',
        itemStyle: { color: 'rgba(0, 122, 255, 0.5)' } // Blue
      },
      {
        name: '판매 스킬',
        type: 'bar',
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: salesHist.counts,
        barWidth: '95%',
        itemStyle: { color: 'rgba(52, 199, 89, 0.5)' } // Green
      },
    ]
  };

  const pkBox = calculateBoxplotStats(pkScores);
  const salesBox = calculateBoxplotStats(salesScores);

  const diffHistOption = {
    title: {
        text: '점수 차이 (PK - 판매 스킬)',
        left: 'center',
        top: '0%',
        textStyle: {
            fontSize: 14,
            fontWeight: 600,
            color: '#1d1d1f',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: '{b} ~ {c} (Range Start ~ Count)'
    },
    grid: {
        top: '25%',
        bottom: '15%',
        left: '10%',
        right: '10%'
    },
    xAxis: {
        type: 'category',
        data: diffHist.labels,
        name: '점수 차이 (PK > Sales ... PK < Sales)',
        nameLocation: 'middle',
        nameGap: 25,
        axisLabel: { color: '#86868b' }
    },
    yAxis: {
        type: 'value',
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } }
    },
    series: [
        {
            type: 'bar',
            data: diffHist.counts,
            itemStyle: {
                color: (params) => {
                    const val = parseFloat(params.name);
                    // Blue if > 0 (PK Higher), Green if < 0 (Sales Higher)
                    return val >= 0 ? '#007AFF' : '#34C759'; 
                }
            },
            markLine: {
                symbol: 'none',
                data: [
                    { xAxis: diffHist.labels.indexOf("0") > -1 ? "0" : undefined, label: { formatter: '0' }, lineStyle: { color: '#333', type: 'solid' } }
                ]
            }
        }
    ]
  };

  const boxplotOption = {
    title: {
        text: '점수 분포 비교 (Boxplot)',
        left: 'center',
        top: '0%',
        textStyle: {
            fontSize: 14,
            fontWeight: 600,
            color: '#1d1d1f',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }
    },
    tooltip: { trigger: 'item', axisPointer: { type: 'shadow' } },
    grid: { left: '10%', right: '10%', top: '18%', bottom: '10%' },
    xAxis: {
        type: 'category',
        data: ['제품 지식(PK)', '판매 스킬'],
        boundaryGap: true,
        splitArea: { show: false },
        splitLine: { show: false },
        axisLabel: { color: '#86868b' }
    },
    yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: '점수',
        splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.05)' } }
    },
    series: [
        {
            name: 'boxplot',
            type: 'boxplot',
            data: [
                {
                    value: pkBox.boxData,
                    itemStyle: { color: 'transparent', borderColor: '#007AFF', borderWidth: 2 }
                },
                {
                    value: salesBox.boxData,
                    itemStyle: { color: 'transparent', borderColor: '#34C759', borderWidth: 2 }
                }
            ]
        },
        {
            name: 'outlier',
            type: 'scatter',
            data: [
                ...pkBox.outliers.map(v => ({ value: [0, v], itemStyle: { color: '#007AFF', opacity: 0.6 } })),
                ...salesBox.outliers.map(v => ({ value: [1, v], itemStyle: { color: '#34C759', opacity: 0.6 } }))
            ]
        }
    ]
  };

  return (
    <div style={{ backgroundColor: '#f5f5f7', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Container>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 20px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FFF0E0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="chart bar" style={{ margin: 0, color: '#FF9500', fontSize: '18px' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>
                제품 지식 vs 판매 스킬 상관관계 분석
              </h2>
              <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                제품 지식 수준(PK)과 판매 스킬(Sales Soft Skill) 점수 간의 상관관계 통계
              </p>
            </div>
          </div>
        </Container>
      </div>

      <Container style={{ padding: '20px 20px 40px 20px', flex: 1 }}>
        <Grid columns={2} stackable stretched style={{ marginBottom: '20px' }}>
          <Grid.Column>
            <Segment padded raised style={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Statistic color="orange" size="small" style={{ width: '100%' }}>
                <Statistic.Label style={{ color: '#86868b', marginBottom: '5px' }}>분석 대상 세션 수</Statistic.Label>
                <Statistic.Value style={{ color: '#1d1d1f' }}>{overview.count}</Statistic.Value>
              </Statistic>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment padded raised style={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
               <Statistic color={overview.correlation >= 0.5 ? 'green' : overview.correlation >= 0.3 ? 'yellow' : 'grey'} size="small" style={{ width: '100%' }}>
                <Statistic.Label style={{ color: '#86868b', marginBottom: '5px' }}>
                    상관계수 (Pearson r)
                    <Popup
                      trigger={<Icon name="info circle" style={{marginLeft: '4px', fontSize: '0.8em', cursor: 'help'}} />}
                      content="1에 가까울수록 강한 양의 상관관계(PK점수가 높으면 판매스킬도 높음). 0에 가까우면 관계 없음."
                      position="bottom center"
                    />
                </Statistic.Label>
                <Statistic.Value style={{ color: '#1d1d1f' }}>{overview.correlation}</Statistic.Value>
              </Statistic>
            </Segment>
          </Grid.Column>
        </Grid>

        <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '15px', color: '#1d1d1f', marginLeft: '5px' }}>점수 상관도 및 분포</h3>
        <Segment raised style={{ padding: '20px', marginBottom: '30px', borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
             <Grid columns={2} stackable divided>
               <Grid.Column width={8}>
                   <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1' }}>
                     <ReactECharts option={scatterOption} style={{ height: '100%', width: '100%' }} />
                   </div>
               </Grid.Column>
               <Grid.Column width={8}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '12px' }}>
                        <div style={{ position: 'relative', width: '100%', flex: 1 }}>
                             <ReactECharts option={boxplotOption} style={{ height: '100%', width: '100%' }} />
                        </div>
                        <div style={{ position: 'relative', width: '100%', flex: 1 }}>
                             <ReactECharts option={diffHistOption} style={{ height: '100%', width: '100%' }} />
                        </div>
                    </div>
               </Grid.Column>
             </Grid>
        </Segment>
      </Container>
    </div>
  );
};

export default PKSalesAnalytics;
